import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { partnerService, programService } from '../services/api';
import { Partner, Program } from '../types';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { PhoneField } from '../components/PhoneField';

interface PartnerForm {
  name: string;
  partner_type: string;
  website: string;
  email: string;
  phone: string;
  country: string;
  city: string;
  notes: string;
  is_active: boolean;
  program_ids: number[];
  contacts: Array<{
    id?: number;
    name: string;
    email?: string;
    phone?: string;
    role_title?: string;
    is_primary?: boolean;
  }>;
}

const initialForm: PartnerForm = {
  name: '',
  partner_type: 'HOST_UNIVERSITY',
  website: '',
  email: '',
  phone: '',
  country: '',
  city: '',
  notes: '',
  is_active: true,
  program_ids: [],
  contacts: []
};

const PartnersManagement: React.FC = () => {
  const { user } = useAuth();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [partnerTypes, setPartnerTypes] = useState<string[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);
  const [formData, setFormData] = useState<PartnerForm>(initialForm);

  useEffect(() => {
    if (user?.is_admin) {
      initialize();
    }
  }, [user]);

  const initialize = async () => {
    try {
      setLoading(true);
      const [list, types] = await Promise.all([
        partnerService.getPartners(),
        partnerService.getPartnerTypes()
      ]);
      setPartners(list);
      setPartnerTypes(types);
      try {
        const programList = await programService.getPrograms();
        setPrograms(programList);
      } catch (err) {
        console.warn('Failed to load programs for partners', err);
      }
    } catch (err) {
      console.error('Error loading partners', err);
      setError('Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleProgramToggle = (programId: number) => {
    setFormData(prev => ({
      ...prev,
      program_ids: prev.program_ids.includes(programId)
        ? prev.program_ids.filter(id => id !== programId)
        : [...prev.program_ids, programId]
    }));
  };
  const handlePhoneChange = (field: 'phone', value?: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value || ''
    }));
  };

  const handleContactChange = (index: number, field: 'name' | 'email' | 'phone' | 'role_title' | 'is_primary', value: string | boolean) => {
    setFormData(prev => {
      const next = [...prev.contacts];
      next[index] = {
        ...next[index],
        [field]: value
      };
      return { ...prev, contacts: next };
    });
  };

  const addContact = () => {
    setFormData(prev => ({
      ...prev,
      contacts: [
        ...prev.contacts,
        { name: '', email: '', phone: '', role_title: '', is_primary: false }
      ]
    }));
  };

  const removeContact = (index: number) => {
    setFormData(prev => ({
      ...prev,
      contacts: prev.contacts.filter((_, i) => i !== index)
    }));
  };

  const resetForm = () => {
    setFormData(initialForm);
    setEditingPartner(null);
    setShowForm(false);
  };

  const handleEdit = (partner: Partner) => {
    setFormData({
      name: partner.name,
      partner_type: partner.partner_type,
      website: partner.website || '',
      email: partner.email || '',
      phone: partner.phone || '',
      country: partner.country || '',
      city: partner.city || '',
      notes: partner.notes || '',
      is_active: partner.is_active,
      program_ids: partner.program_ids || [],
      contacts: (partner.contacts || []).map(contact => ({
        id: contact.id,
        name: contact.name,
        email: contact.email || '',
        phone: contact.phone || '',
        role_title: contact.role_title || '',
        is_primary: contact.is_primary
      }))
    });
    setEditingPartner(partner);
    setShowForm(true);
  };

  const handleDelete = async (partner: Partner) => {
    if (!window.confirm(`Delete partner ${partner.name}?`)) return;
    try {
      await partnerService.deletePartner(partner.id);
      await initialize();
    } catch (err) {
      console.error('Failed to delete partner', err);
      setError('Failed to delete partner');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      if (editingPartner) {
        await partnerService.updatePartner(editingPartner.id, formData);
      } else {
        await partnerService.createPartner(formData);
      }
      await initialize();
      resetForm();
    } catch (err) {
      console.error('Failed to save partner', err);
      setError('Failed to save partner');
    }
  };

  if (!user?.is_admin) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">Access denied. Admin privileges required.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading partners...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Partners Management</h1>
          <p className="text-sm text-gray-600 dark:text-slate-300 mt-1">Maintain partner records, contacts, and program links.</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 px-4 py-2"
        >
          <PlusIcon className="w-5 h-5" />
          Add Partner
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-600">
          {error}
        </div>
      )}

      {showForm && (
        <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
            <h2 className="text-lg font-medium text-slate-900 dark:text-slate-50">{editingPartner ? 'Edit Partner' : 'Create Partner'}</h2>
            <button onClick={resetForm} className="text-sm text-gray-500 hover:text-gray-700">Cancel</button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-6 p-6">
            <div>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Partner Details</h3>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Partner Type *</label>
                  <select
                    name="partner_type"
                    value={formData.partner_type}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {partnerTypes.map(type => (
                      <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Contact</h3>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <PhoneField
                  label="Phone"
                  name="phone"
                  value={formData.phone || undefined}
                  onChange={(value) => handlePhoneChange('phone', value)}
                  hideValidationMessage
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Contacts</h3>
                <button
                  type="button"
                  onClick={addContact}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  + Add Contact
                </button>
              </div>
              {formData.contacts.length === 0 && (
                <p className="mt-2 text-sm text-gray-500">No contacts yet.</p>
              )}
              <div className="mt-3 space-y-3">
                {formData.contacts.map((contact, index) => (
                  <div key={index} className="rounded-lg border border-gray-200 p-3">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
                        <input
                          type="text"
                          value={contact.name}
                          onChange={(e) => handleContactChange(index, 'name', e.target.value)}
                          className="w-full border border-gray-300 rounded px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                        <input
                          type="email"
                          value={contact.email || ''}
                          onChange={(e) => handleContactChange(index, 'email', e.target.value)}
                          className="w-full border border-gray-300 rounded px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                        <PhoneField
                          value={contact.phone || ''}
                          onChange={(value) => handleContactChange(index, 'phone', value || '')}
                          inputClassName="w-full border border-gray-300 rounded px-3 py-2"
                          hideValidationMessage
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Role/Title</label>
                        <input
                          type="text"
                          value={contact.role_title || ''}
                          onChange={(e) => handleContactChange(index, 'role_title', e.target.value)}
                          className="w-full border border-gray-300 rounded px-3 py-2"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="inline-flex items-center gap-2 text-xs text-gray-600">
                          <input
                            type="checkbox"
                            checked={Boolean(contact.is_primary)}
                            onChange={(e) => handleContactChange(index, 'is_primary', e.target.checked)}
                            className="rounded border-gray-300 text-blue-600"
                          />
                          Primary
                        </label>
                        <button
                          type="button"
                          onClick={() => removeContact(index)}
                          className="text-xs text-red-600 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Location</h3>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Associated Programs</label>
              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3 space-y-2">
                {programs.length === 0 && (
                  <p className="text-sm text-gray-500">No programs available.</p>
                )}
                {programs.map(program => (
                  <label key={program.id} className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      checked={formData.program_ids.includes(program.id)}
                      onChange={() => handleProgramToggle(program.id)}
                    />
                    <span>{program.title}</span>
                  </label>
                ))}
              </div>
            </div>

            {editingPartner && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Associated Professors</h3>
                <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  {((!editingPartner.host_professors || editingPartner.host_professors.length === 0) && (!editingPartner.guest_professors || editingPartner.guest_professors.length === 0)) && (
                    <p className="text-sm text-gray-500">No associated professors yet.</p>
                  )}
                  {(editingPartner.host_professors && editingPartner.host_professors.length > 0) && (
                    <>
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Host Professors</p>
                      <ul className="space-y-2 text-sm text-gray-700 mb-3">
                        {editingPartner.host_professors.map((prof) => {
                          const name = [prof.first_name, prof.last_name].filter(Boolean).join(' ').trim();
                          const label = name || prof.email || `User #${prof.user_id}`;
                          return (
                            <li key={prof.user_id} className="flex items-center justify-between">
                              <span>{label}</span>
                              <span className="text-xs text-gray-500">Host</span>
                            </li>
                          );
                        })}
                      </ul>
                    </>
                  )}
                  {(editingPartner.guest_professors && editingPartner.guest_professors.length > 0) && (
                    <>
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Guest Professors</p>
                      <ul className="space-y-2 text-sm text-gray-700">
                        {editingPartner.guest_professors.map((prof) => {
                          const name = [prof.first_name, prof.last_name].filter(Boolean).join(' ').trim();
                          const label = name || prof.email || `User #${prof.user_id}`;
                          return (
                            <li key={prof.user_id} className="flex items-center justify-between">
                              <span>{label}</span>
                              <span className="text-xs text-gray-500">{prof.role ? prof.role.replace(/_/g, ' ') : 'Guest'}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </>
                  )}
                </div>
              </div>
            )}

            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Active</span>
            </label>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={resetForm}
                className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 px-4 py-2"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 px-4 py-2"
              >
                {editingPartner ? 'Update Partner' : 'Create Partner'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
          <h2 className="text-lg font-medium text-slate-900 dark:text-slate-50">All partners</h2>
          <p className="text-xs text-gray-500">{partners.length} total</p>
        </div>
        <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700/60">
          <thead className="bg-gray-50 dark:bg-slate-900">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-200 uppercase tracking-wider">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-200 uppercase tracking-wider">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-200 uppercase tracking-wider">Location</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-200 uppercase tracking-wider">Programs</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-slate-200 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-slate-200 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-slate-900/60 divide-y divide-gray-200 dark:divide-slate-700/60">
            {partners.map(partner => (
              <tr
                key={partner.id}
                className="cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800/60"
                onClick={() => handleEdit(partner)}
              >
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">{partner.name}</p>
                  {partner.email && <p className="text-sm text-gray-500">{partner.email}</p>}
                  {partner.guest_professors && partner.guest_professors.length > 0 && (
                    <p className="text-xs text-gray-400">Guest professors: {partner.guest_professors.length}</p>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">{partner.partner_type.replace('_', ' ')}</td>
                <td className="px-4 py-3 text-sm text-gray-700">
                  {[partner.city, partner.country].filter(Boolean).join(', ') || '—'}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">
                  {partner.program_ids.length > 0 ? `${partner.program_ids.length} linked` : 'None'}
                </td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    partner.is_active
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200'
                      : 'bg-gray-200 text-gray-600 dark:bg-slate-700/60 dark:text-slate-200'
                  }`}>
                    {partner.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right space-x-2" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => handleEdit(partner)}
                    className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 px-3 py-1 text-sm"
                  >
                    <PencilIcon className="w-4 h-4 mr-1" /> Edit
                  </button>
                  <button
                    onClick={() => handleDelete(partner)}
                    className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-red-500 text-white border border-red-500 hover:bg-red-600 hover:border-red-600 px-3 py-1 text-sm"
                  >
                    <TrashIcon className="w-4 h-4 mr-1" /> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PartnersManagement;
